import { useAppDispatch } from 'src/app/store/hooks';
import {
  PricingSchemeRead,
  usePricingPricingSchemesCreateMutation,
  usePricingPricingSchemesPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { configurationFormValuesToPricingScheme } from './configurationFormValuesToPricingScheme';

import type { PricingWizardMode } from './PricingWizard';
import type { SchemeConfigFormValues } from './PricingWizardSchemeConfigurationStep';
import type { SchemeMetaFormValues } from './PricingWizardSchemeMetaStep';

type PricingWizardFormSubmitProps = {
  mode: PricingWizardMode;
  workInProgressScheme: SchemeMetaFormValues;
  originalPricingSchemeValues?: PricingSchemeRead;
  onSubmit: (values: SchemeConfigFormValues) => void;
  setFormError: (error: string) => void;
};

export const usePricingWizardFormSubmit = ({
  mode,
  workInProgressScheme,
  originalPricingSchemeValues,
  onSubmit,
  setFormError,
}: PricingWizardFormSubmitProps) => {
  const dispatch = useAppDispatch();
  const [createScheme] = usePricingPricingSchemesCreateMutation();
  const [updateScheme] = usePricingPricingSchemesPartialUpdateMutation();

  const submitForm = async (values: SchemeConfigFormValues) => {
    const pricingScheme = configurationFormValuesToPricingScheme(
      values,
      workInProgressScheme,
      originalPricingSchemeValues?.id
    );
    if (mode === 'create' || mode === 'clone') {
      try {
        const newSchemeResponse = await createScheme({ pricingScheme });
        if ('error' in newSchemeResponse) {
          return setFormError(formatServerError(newSchemeResponse.error));
        }
        onSubmit(newSchemeResponse.data);
        dispatch(
          show({
            title: `Successfully created pricing`,
            type: 'success',
          })
        );
      } catch (e) {
        setFormError(formatServerError(e));
      }
    } else if (originalPricingSchemeValues?.id) {
      try {
        const updatedSchemeResponse = await updateScheme({
          id: originalPricingSchemeValues.id,
          patchedPricingScheme: pricingScheme,
        });
        if ('error' in updatedSchemeResponse) {
          return setFormError(formatServerError(updatedSchemeResponse.error));
        }
        onSubmit(pricingScheme);
        dispatch(
          show({
            title: `Successfully updated pricing`,
            type: 'success',
          })
        );
      } catch (e) {
        setFormError(formatServerError(e));
      }
    }
  };

  return {
    submitForm,
  };
};
